import React, { useState, useEffect } from "react";
import { Box, Typography, Button, FormControl } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import FormModal from "../../../components/FormModal/FormModal";
import Select from "../../../components/Select/Select";

const initialData = {
    value: "",
    label: "Selecciona una empresa",
};

export default function DialogSelect({ open, setOpen, title, data = [], onClick, setValue, value }) {
    const [request, setRequest] = useState("Personal");
    const [companyList, setCompanyList] = useState([]);

    const handleClose = () => {
        setOpen(false);
    };

    const handleChangeCompanyType = (e) => {
        setValue(e.target.value);
    };

    useEffect(() => {
        if (data) {
            const reducedArray = data.reduce((acc, curr) => {
                if (!acc.some((item) => item.value === curr.citizen)) {
                    acc.push({ value: `${curr.id},${curr.company_name}`, label: curr.company_name });
                }
                return acc;
            }, []);
            setCompanyList([initialData, ...reducedArray]);
        }
    }, [data]);

    const isEmpresaSelected = request === "Empresa";
    const canContinue = isEmpresaSelected ? Boolean(value && value !== "") : true;

    return (
        <FormModal 
            conditionalClose={true} 
            open={open} 
            onClose={handleClose} 
            title={title || "¿Cómo deseas continuar?"}
            maxWidth="sm"
            fullWidth={true}
        >
            <Box sx={{ padding: "8px 12px 20px 12px", fontFamily: "Roboto, sans-serif" }}>
                <Typography variant="body2" sx={{ color: "#57697F", fontSize: "14.5px", mb: 3, fontWeight: 500 }}>
                    Selecciona la modalidad bajo la cual deseas realizar esta solicitud:
                </Typography>

                {/* Option Cards Grid */}
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 3 }}>
                    {/* Personal Option */}
                    <Box
                        onClick={() => {
                            setRequest("Personal");
                            setValue("");
                        }}
                        sx={{
                            border: request === "Personal" ? "2px solid #0063bc" : "1px solid #E1E9EC",
                            borderRadius: "14px",
                            padding: "18px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                            cursor: "pointer",
                            backgroundColor: request === "Personal" ? "#F0F4FF" : "#FFFFFF",
                            boxShadow: request === "Personal" ? "0px 4px 12px rgba(0, 99, 188, 0.12)" : "0px 2px 4px rgba(0,0,0,0.02)",
                            transition: "all 0.2s ease",
                            '&:hover': {
                                borderColor: "#0063bc"
                            }
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: "10px",
                                    backgroundColor: request === "Personal" ? "#0063bc" : "#E3F4F6",
                                    color: request === "Personal" ? "#FFFFFF" : "#0063bc",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0
                                }}
                            >
                                <PersonIcon />
                            </Box>
                            <Typography sx={{ fontWeight: 700, fontSize: "14.5px", color: "#0F2350" }}>
                                Personal
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: "12px", color: "#57697F", lineHeight: "16px" }}>
                            Solicitud realizada a título personal como ciudadano.
                        </Typography>
                    </Box>

                    {/* Empresa Option */}
                    <Box
                        onClick={() => {
                            if (data && data.length > 0) {
                                setRequest("Empresa");
                            }
                        }}
                        sx={{
                            border: isEmpresaSelected ? "2px solid #0063bc" : "1px solid #E1E9EC",
                            borderRadius: "14px",
                            padding: "18px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                            cursor: data && data.length > 0 ? "pointer" : "not-allowed",
                            opacity: data && data.length > 0 ? 1 : 0.6,
                            backgroundColor: isEmpresaSelected ? "#F0F4FF" : "#FFFFFF",
                            boxShadow: isEmpresaSelected ? "0px 4px 12px rgba(0, 99, 188, 0.12)" : "0px 2px 4px rgba(0,0,0,0.02)",
                            transition: "all 0.2s ease",
                            '&:hover': {
                                borderColor: data && data.length > 0 ? "#0063bc" : "#E1E9EC"
                            }
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: "10px",
                                    backgroundColor: isEmpresaSelected ? "#0063bc" : "#E3F4F6",
                                    color: isEmpresaSelected ? "#FFFFFF" : "#0063bc",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0
                                }}
                            >
                                <BusinessIcon />
                            </Box>
                            <Typography sx={{ fontWeight: 700, fontSize: "14.5px", color: "#0F2350" }}>
                                Empresa
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: "12px", color: "#57697F", lineHeight: "16px" }}>
                            {data && data.length > 0 
                                ? "Solicitud en representación de una persona jurídica." 
                                : "No tienes empresas registradas asociadas a tu usuario."}
                        </Typography>
                    </Box>
                </Box>

                {/* Company Select Dropdown */}
                {isEmpresaSelected && (
                    <Box sx={{ mt: 2.5, mb: 2 }}>
                        <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: "#0F2350", mb: 1 }}>
                            Selecciona la empresa correspondiente:
                        </Typography>
                        <FormControl fullWidth size="small">
                            <Select 
                                id="selectCompany" 
                                data={companyList} 
                                value={value} 
                                onChange={handleChangeCompanyType} 
                                disableEmptyValue 
                            />
                        </FormControl>
                    </Box>
                )}

                {/* Action Buttons Footer */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 4, pt: 2, borderTop: "1px solid #E1E9EC" }}>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        sx={{
                            color: "#57697F",
                            borderColor: "#CFDCE0",
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: 700,
                            px: 3
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!canContinue}
                        onClick={onClick}
                        sx={{
                            bgcolor: "#0F2350",
                            color: "#FFFFFF",
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: 700,
                            px: 4,
                            '&:hover': {
                                bgcolor: "#EA361A"
                            }
                        }}
                    >
                        Continuar
                    </Button>
                </Box>
            </Box>
        </FormModal>
    );
}
